import { useCallback, useEffect, useRef, useState } from 'react';
import { mapValues } from 'lodash';
import * as R from 'remeda';
import { LocationDescriptor, LocationState, Path } from 'history';
import { useHistory, useLocation } from 'react-router';
import { parse, stringify } from '../../lib/url';
import { StorageKey } from '../../redux/modules/localStorage/actions';

const getArrayFromValue = (
  value: null | undefined | string | string[]
): string[] | undefined =>
  value ? (Array.isArray(value) ? value : [value]) : undefined;

export const getStringFromValue = (
  value: null | undefined | string | Array<string | null>
): string | undefined =>
  value ? (Array.isArray(value) ? (value[0] ?? undefined) : value) : undefined;

type UseStoredStateProps<
  T extends Partial<Record<K1, string> & Record<K2, string[]>>,
  K1 extends string,
  K2 extends string,
> = {
  name: StorageKey;
  defaultValues: T;
  stringKeys?: K1[];
  arrayKeys?: K2[];
  historyFn?: (
    location: Path | LocationDescriptor<LocationState>,
    state?: LocationState
  ) => void;
  parseFn?: typeof parse;
  omitFromLocalStorage?: K1[];
};

/**
 * @deprecated
 * use src/utils/hooks/useStoredQuery.ts instead
 */
export const useStoredState = <
  T extends Partial<Record<K1, string> & Record<K2, string[]>>,
  K1 extends string = never,
  K2 extends string = never,
>({
  name,
  defaultValues,
  arrayKeys = [],
  stringKeys = [],
  historyFn,
  parseFn = parse,
  omitFromLocalStorage,
}: UseStoredStateProps<T, K1, K2>) => {
  const searchQueryData = useLocation().search;
  const { replace } = useHistory();

  const deriveState = useCallback(
    (
      search: string,
      allowLocalStorageFallback: boolean
    ): Partial<Record<K1, string> & Record<K2, string[]>> => {
      const localStorageData = allowLocalStorageFallback
        ? localStorage.getItem(name)
        : null;
      const savedState = parseFn(search || localStorageData || '');
      const strings = mapValues(
        R.pick(savedState, stringKeys),
        getStringFromValue
      );
      const arrays = mapValues(
        R.pick(savedState, arrayKeys),
        getArrayFromValue
      );

      return {
        ...defaultValues,
        ...arrays,
        ...strings,
      };
    },
    [
      name,
      parseFn,
      ...stringKeys,
      ...arrayKeys,
      ...Object.values(defaultValues),
    ]
  );

  const [internalState, setInternalState] = useState<
    Partial<Record<K1, string> & Record<K2, string[]>>
  >(() => deriveState(searchQueryData, true));

  // answers: "was this URL written by us or by the user?"
  const lastWrittenSearchRef = useRef(searchQueryData.replace(/^\?/, ''));
  // answers: "is this state a correction of a URL we just adopted?"
  const urlDerivedQueryRef = useRef<string | null>(null);
  // answers: "did the location actually change, or is this just a re-render?"
  const lastSeenSearchRef = useRef(searchQueryData);

  const persistState = useCallback(
    (state: Record<string, string | string[] | undefined>) => {
      const locationQuery = stringify(state);
      const localStorageQuery = stringify(
        R.omit(state, omitFromLocalStorage ?? [])
      );
      localStorage.setItem(name, localStorageQuery);
      const urlDerivedQuery = urlDerivedQueryRef.current;
      urlDerivedQueryRef.current = null;
      if (!historyFn || urlDerivedQuery === locationQuery) return;
      // The same query never needs writing twice. Without this, a caller passing
      // an unstable `historyFn` re-runs this effect on every render and piles up
      // identical history entries.
      if (locationQuery === lastWrittenSearchRef.current) return;
      if (urlDerivedQuery !== null) {
        replace({ search: locationQuery });
      } else {
        historyFn({ search: locationQuery });
      }
      lastWrittenSearchRef.current = locationQuery;
    },
    [omitFromLocalStorage, name, historyFn, replace]
  );

  useEffect(() => {
    if (searchQueryData === lastSeenSearchRef.current) return;

    lastSeenSearchRef.current = searchQueryData;

    const search = searchQueryData.replace(/^\?/, '');
    if (search === lastWrittenSearchRef.current) return;

    lastWrittenSearchRef.current = search;
    const derivedState = deriveState(searchQueryData, false);
    if (R.isDeepEqual(internalState, derivedState)) return;

    urlDerivedQueryRef.current = stringify(derivedState);
    setInternalState(derivedState);
  }, [searchQueryData, deriveState, internalState]);

  useEffect(() => persistState(internalState), [internalState, persistState]);

  return [internalState, setInternalState] as const;
};
